/**
 * Root Route
 *
 * The root layout that wraps the entire application.
 * Provides AuthProvider context to all child routes.
 * Updates router context with auth state for route guards.
 */

import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "../stores/authStore";
import { useAuthInit } from "../hooks/useAuthInit";
import { ConsentTagManager } from "../components/compliance/ConsentTagManager";
import { JsonLd } from "../seo/JsonLd";
import { organizationSchema } from "../seo/schemas";
import type { RouterContext } from "../router";
import { router } from "../router";

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  return (
    <AuthProvider>
      <AuthInitializer />
      <AuthContextSync />
      <ConsentTagManager />
      {/* Organization JSON-LD on every page (Story 30.4) — the same entity
          everywhere for knowledge-graph consistency; prerender captures it on
          public pages, app pages are noindex so it's harmless there. */}
      <JsonLd data={organizationSchema()} />
      <Outlet />
      {import.meta.env.DEV && !import.meta.env.VITE_E2E_MODE && (
        <TanStackRouterDevtools position="bottom-right" />
      )}
    </AuthProvider>
  );
}

/**
 * Initializes authentication state on app load
 * Attempts silent refresh using HttpOnly cookie
 */
function AuthInitializer() {
  useAuthInit();
  return null;
}

/**
 * Syncs auth state from React context to router context
 * This enables route guards to access auth state in beforeLoad hooks
 */
function AuthContextSync() {
  const auth = useAuth();

  useEffect(() => {
    // Update router context when auth state changes
    router.update({
      context: {
        auth: {
          isAuthenticated: auth.isAuthenticated,
          isLoading: auth.isLoading,
          user: auth.user,
        },
      },
    });
  }, [auth.isAuthenticated, auth.isLoading, auth.user]);

  return null;
}
